import React from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Adg4Button } from "@/components/adg4/button/src";
import { analyticsLabels } from "@/components/global-nav/user-profile-new/src";
import { Link } from "@/libraries/design/components";
import { useLocalizedHref } from "@/libraries/web-framework";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src";
import { AtlassianLogoArrowLinkBrand } from "@/libraries/wpl/foundations/src/logos/atlassian-logo-arrow-link-brand";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import { GlobalNavLinkContentProps } from "../nav-links-container";
import SearchButton from "../search-button";

import { Divider } from "./icons/divider";
import { DropdownType } from "./mobile-nav";
import { MobileNavLink } from "./mobile-nav-link";

const linkContainerCss = css({
    listStyle: "none",
    padding: "0 0 24px 0",
    margin: "0",
    fontWeight: "700",
    fontSize: "14px"
});

const signInContainerCss = css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBlockStart: "8px",
    paddingBlockEnd: "8px",
    [media.above.sm]: {
        display: "none"
    }
});

const signInContainerCssWithFeatureFlag = css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingBlockStart: "8px",
    paddingBlockEnd: "8px",
    [media.above.sm]: {
        display: "none"
    }
});

const returnToHomeStyle = css({
    display: "none",
    [media.below.sm]: {
        display: "block"
    }
});

const getItFreeCss = css({
    display: "none",
    [media.below.sm]: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBlockStart: "8px",
        marginBlockEnd: "8px",
        height: "36px"
    }
});

const atlassianArrowLinkContainerStyle = css({
    height: "63.29px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
});

const atlassianArrowLinkContainerStyleWithFeatureFlag = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingBlockStart: "32px"
});

const signInCss = css({
    paddingBlock: token("space.200")
});

const signInCssWithFeatureFlag = css({
    paddingBlock: token("space.400"),
    alignItems: "center"
});

export type MobileNavLinkContentProps = {
    readonly globalLinksKey: string;
    readonly linkText: string;
    readonly hasSubnav: boolean;
    readonly href?: string;
    readonly productKey?: string;
    readonly dropdownType?: DropdownType;
};

export type MobileNavLinksContainerInfo = {
    mobileLinkKey: string;
    mobileLinksProps: MobileNavLinkContentProps[];
};

interface MobileNavLinksContainerProps extends MobileNavLinksContainerInfo {
    handleMenuPage: (menuPage: DropdownType) => void;
    isIncludeBottomDivider?: boolean;
}

export const MobileNavLinksContainer = ({
    handleMenuPage,
    mobileLinkKey,
    mobileLinksProps,
    isIncludeBottomDivider = true
}: MobileNavLinksContainerProps) => {
    return (
        <>
            <ul className={mobileLinkKey} css={linkContainerCss}>
                {mobileLinksProps.map(
                    (mobileLink: GlobalNavLinkContentProps, index) => {
                        return (
                            <li
                                className="nav-link"
                                key={`${mobileLink.globalLinksKey}-${index}`}
                            >
                                <MobileNavLink
                                    handleMenuPage={handleMenuPage}
                                    {...mobileLink}
                                />
                            </li>
                        );
                    }
                )}
            </ul>
            {isIncludeBottomDivider && <Divider />}
        </>
    );
};

type MobileNavLinksProps = {
    isLoggedIn: boolean;
    handleMenuPage: (menuPage: DropdownType | string) => void;
    mobileLinksSections: MobileNavLinksContainerInfo[];
    productKey?: ProductKeyTypes;
    isIncludeCTA?: boolean;
    ctaText?: string;
    ctaUrl?: string;
    ctaOnClick?: (e: React.MouseEvent) => void;
    isIncludeAtlassianLogo?: boolean;
    isShowSearchButton?: boolean;
};
// eslint-disable-next-line complexity
export const MobileNavLinks = ({
    isLoggedIn,
    handleMenuPage,
    mobileLinksSections,
    productKey = "jira",
    isIncludeCTA = false,
    ctaText = "Get it free",
    ctaUrl = "/software/jira/free",
    ctaOnClick,
    isIncludeAtlassianLogo = false,
    isShowSearchButton = true
}: MobileNavLinksProps) => {
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const baseUrl = process.env["NEXT_PUBLIC_IDENTITY_BASE_URL"];
    const stargateBaseUrl = process.env["NEXT_PUBLIC_API_PRIVATE_BASE_URL"];
    const continueUrl = stargateBaseUrl + "/start/authredirect";
    const signInUrl = `${baseUrl}/login?continue=${encodeURIComponent(
        continueUrl
    )}`;

    const homeHref = useLocalizedHref("/");
    const signinLink = {
        text: intl.formatMessage({
            id: "globalnavv2.mobile.navlink.signin",
            defaultMessage: "Sign in"
        }),
        href: signInUrl
    };

    const onLogInClick = () => {
        createAnalyticsEvent({
            action: "clicked",
            name: "bxp.globalnavdropdown.click.login",
            label: analyticsLabels["JoinSiteUserDropdown.logIn"]
        }).fire();
    };

    const isMobileSearchImprovementsGate = checkFeatureFlagGate(
        featureFlags.mobileSearchImprovementsGate
    );
    if (isMobileSearchImprovementsGate) {
        isShowSearchButton = false;
    }

    return (
        <div>
            {isIncludeCTA && (
                <div css={getItFreeCss}>
                    <Adg4Button
                        className="mobile-nav-dropdown-menu-cta"
                        variant="primary"
                        href={ctaOnClick ? undefined : ctaUrl}
                        size={
                            isMobileSearchImprovementsGate ? undefined : "small"
                        }
                        dataEventProps={{
                            "data-product-key": productKey,
                            "data-event": "clicked",
                            "data-event-component": "linkButton",
                            "data-event-container": "navbar",
                            "data-label": ctaText,
                            "data-label-english": "Get it free",
                            "data-track-event": `${productKey}.get-it-free-button-clicked`
                        }}
                        style={{
                            width: "100%",
                            fontSize: "1rem",
                            lineHeight: "1.25rem"
                        }}
                        onClick={ctaOnClick}
                    >
                        {ctaText}
                    </Adg4Button>
                </div>
            )}
            {mobileLinksSections.map(
                ({ mobileLinkKey, mobileLinksProps }, index) => (
                    <React.Fragment key={index}>
                        <MobileNavLinksContainer
                            mobileLinkKey={mobileLinkKey}
                            mobileLinksProps={mobileLinksProps}
                            handleMenuPage={handleMenuPage}
                            isIncludeBottomDivider={false}
                        />
                    </React.Fragment>
                )
            )}
            {/* Add divider above Atlassian logo when feature flag is enabled */}
            {checkFeatureFlagGate(
                featureFlags.mobileSearchImprovementsGate
            ) && <Divider />}
            {isIncludeAtlassianLogo && (
                <div css={returnToHomeStyle}>
                    <div
                        css={[
                            atlassianArrowLinkContainerStyle,
                            isMobileSearchImprovementsGate &&
                                atlassianArrowLinkContainerStyleWithFeatureFlag
                        ]}
                    >
                        <a
                            className="navigation-bar-atlas-icon"
                            href={homeHref}
                            aria-label={intl.formatMessage({
                                id: "productPageNav.atlassian-logo-label",
                                defaultMessage: "Atlassian logo"
                            })}
                            data-event="clicked"
                            data-event-component="linkButton"
                            data-event-container="productPageMobileNav"
                            data-product-key="home"
                        >
                            <AtlassianLogoArrowLinkBrand
                                width={"311px"}
                                height={"15.29px"}
                            />
                        </a>
                    </div>
                </div>
            )}
            <div
                css={[
                    signInContainerCss,
                    isMobileSearchImprovementsGate &&
                        signInContainerCssWithFeatureFlag
                ]}
            >
                {isShowSearchButton && (
                    <SearchButton
                        buttonId={"global-nav-search-bar-icon-mobile"}
                        onClick={(e) => {
                            e.preventDefault();
                            handleMenuPage("search");

                            createAnalyticsEvent({
                                eventName: "searchOpen",
                                eventType: "ui",
                                action: "clicked",
                                actionSubject: "button",
                                actionSubjectId: "searchButtonMobile",
                                source: "globalNav",
                                attributes: {
                                    product: "search"
                                }
                            }).fire();
                        }}
                    />
                )}
                {!isLoggedIn && (
                    <Link
                        key={signinLink.text}
                        text={signinLink.text}
                        href={signinLink.href}
                        type="primary"
                        css={[
                            signInCss,
                            isMobileSearchImprovementsGate &&
                                signInCssWithFeatureFlag
                        ]}
                        onClick={onLogInClick}
                        ariaLabel={intl.formatMessage({
                            id: "globalnavv2.mobile.navlink.signin.ariaLabel",
                            defaultMessage: "Sign in link"
                        })}
                        dataEventProps={{
                            "data-product-key": productKey,
                            "data-event-component": "linkButton",
                            "data-event-container": "navbarDropdown",
                            "data-label": signinLink.text,
                            "data-event": "clicked"
                        }}
                    />
                )}
            </div>
        </div>
    );
};
